import {defineStore} from 'pinia';
import {useMenusStore} from "@/new-menu/stores/menus.store";
import API from "@/shared/helpers/API";
import {formatDateOnly, getLocalDateFromServer} from "@/shared/helpers/dates";

export const useReservationsStore = defineStore('reservations', {
	state: () => ({
		experiences: normalizeExperiences(experiences, experiencesTimeslots, experiencesTimeslotsExceptions),
		selectedExperienceId: null,
		chosenDate: null,
		chosenTime: {
			time: null,
			slotId: null,
			maxPeople: null
		},
		chosenPeople: null,
		confirmedReservationId: null,
		customerData: null,
		availabilities: {
			experience_id: null,
			lastDate: null,
			maxCapacity: null,
			reservations: {},
		},
		loadingAvailabilities: false,
	}),
	getters: {
		experiencesAvailable: state => {
			return state.experiences.filter(e => e.active);
		},
		selectedExperience: state => {
			return state.selectedExperienceId ? state.experiences.find(e => e.experience_id === state.selectedExperienceId) : null;
		},
	},
	actions: {
		goToMenu(experienceId, noScroll = false) {
			this.selectedExperienceId = experienceId;
			if(!noScroll) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						const menusStore = useMenusStore();
						menusStore.scrollToMenuTop();
					});
				});
			}
		},
		backToMultimenu() {
			setTimeout(() => {
				this.selectedExperienceId = null;
			}, 500);
		},
		getSlotFromId(slotId) {
			let experience = this.selectedExperience;
			if(experience) {
				let slot = experience.timeslots.find(s => s.experience_timeslot_id === slotId);
				if (!slot) {
					slot = experience.exceptions.flatMap(e => e.timeslots).find(s => s.experience_timeslot_id === slotId);
				}
				return slot;
			}
			return null;
		},
		updateExperienceAvailability() {
			const menusStore = useMenusStore();
			const chosenDate = formatDateOnly(this.chosenDate);
			this.loadingAvailabilities = true;
			API.init().getExperienceAvailability(menusStore.business.business_id, this.selectedExperienceId, chosenDate, (response) => {
				this.loadingAvailabilities = false;
				if(response && response.experience_id && Number(response.experience_id) === this.selectedExperienceId) {
					this.availabilities.experience_id = this.selectedExperienceId;
					response.availability.forEach(reservation => {
						let start = getLocalDateFromServer(chosenDate + " " + reservation.start);
						let end = getLocalDateFromServer(chosenDate + " " + reservation.end);
						reservation.start = start.getHours() + ":" + start.getMinutes();
						reservation.end = end.getHours() + ":" + end.getMinutes();
					});
					this.availabilities.reservations[chosenDate] = response.availability;
					this.availabilities.lastDate = chosenDate;
					this.availabilities.maxCapacity = response.max_capacity;
				}
			});
		},
	}
});

function normalizeExperiences(experiences, timeslots, exceptions) {

	let timeslotsMap = {};
	let timeslotsExceptions = {};
	timeslots.forEach(timeslot => {
		if(timeslot.exception_id) {
			if(!timeslotsExceptions.hasOwnProperty(timeslot.exception_id)) {
				timeslotsExceptions[timeslot.exception_id] = [];
			}
			timeslotsExceptions[timeslot.exception_id].push(normalizeTimeslot(timeslot));
		} else {
			if(!timeslotsMap.hasOwnProperty(timeslot.experience_id)) {
				timeslotsMap[timeslot.experience_id] = [];
			}
			timeslotsMap[timeslot.experience_id].push(normalizeTimeslot(timeslot));
		}
	});

	let exceptionsMap = {};
	const today = formatDateOnly(new Date());
	exceptions.forEach(exception => {
		// Filter out exceptions with dates in the past
		if(exception.date && exception.date >= today) {
			if(!exceptionsMap.hasOwnProperty(exception.experience_id)) {
				exceptionsMap[exception.experience_id] = [];
			}
			exception.timeslots = [];
			if(timeslotsExceptions.hasOwnProperty(exception.exception_id)) {
				exception.timeslots = timeslotsExceptions[exception.exception_id];
			}
			exceptionsMap[exception.experience_id].push(exception);
		}
	});

	let result = experiences.map(experience => {
		experience.price = parseInt(experience.price);
		experience.info_dogs = experience.info_dogs === 1;
		experience.info_children = experience.info_children === 1;
		experience.info_special = experience.info_special === 1;
		experience.info_allergens = experience.info_allergens === 1;
        experience.days_advance = experience.days_advance ? parseInt(experience.days_advance) : 30;
        experience.limit_people = experience.limit_people !== null ? parseInt(experience.limit_people) : null;
        experience.card_min_people = experience.card_min_people !== null ? parseInt(experience.card_min_people) : null;
		experience.picture = experience.picture ? experience.picture : null;
		experience.slug = experience.slug ? experience.slug : null;
		experience.timeslots = [];
		if (timeslotsMap.hasOwnProperty(experience.experience_id)) {
			experience.timeslots = timeslotsMap[experience.experience_id];
		}
		experience.exceptions = [];
		if (exceptionsMap.hasOwnProperty(experience.experience_id)) {
			experience.exceptions = exceptionsMap[experience.experience_id];
		}
		experience.experience_id = parseInt(experience.experience_id);
		return experience;
	});

	return result;
}

function normalizeTimeslot(t) {

	let startTime = t.time_start.split(":");
	if(startTime.length >= 2) {
		startTime = startTime[0] + ":" + startTime[1];
	}
	let endTime = t.time_end.split(":");
	if(endTime.length >= 2) {
		endTime = endTime[0] + ":" + endTime[1];
	}
	return {
		experience_timeslot_id: parseInt(t.experience_timeslot_id),
		exception_id: t.exception_id,
		name: t.name,
		price: t.price ? Number(t.price) : null,
		start: startTime,
		end: endTime,
		weekday: Number(t.weekday),
		time: Number(t.duration),
		intervals: Number(t.intervals),
		dontRequestCard: t.no_card === 1,
	}
}